import { UserProfileRole } from '@@types/api.types';
import { MainLayout } from '@components/layouts/main.layout';
import { CompanyDetailsTile } from '@components/pages/company/components/company-info-card.component';
import { TechnologiesGridTile } from '@components/pages/home/components/technologies-tile.component';
import { ProductsGridTile } from '@components/pages/product/components/products-grid-tile.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { HelmetWrapper } from '@components/shared/helmet.component';
import { LoadingStateContainer } from '@components/shared/loading-state-container.component';
import { PageviewTracker } from '@components/shared/pageview-tracker.component';
import { useIntelOrganizationQuery } from '@queries/intel-organizations.query';
import { useGetCurrentUser } from '@queries/users.query';
import { routes } from '@routes';
import type { FC } from 'react';
import { Navigate, useParams } from 'react-router-dom';

export const IntelOrganizationDetailPage: FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { data: user, isPending: isUserPending } = useGetCurrentUser();
  const isIntel = user?.profile?.role === UserProfileRole.INTELLIGENCE;
  const { data: organization, isPending: isOrganizationPending } = useIntelOrganizationQuery({
    companyId,
    enabled: isIntel,
  });

  if (isUserPending) {
    return null;
  }

  if (!isIntel) {
    return <Navigate to={routes.home.path} />;
  }

  if (!companyId) {
    return <Navigate to={routes.intelOrganizations.path} />;
  }

  return (
    <PageviewTracker>
      <HelmetWrapper title={organization ? `${organization.name} - Organization` : 'Organization'} />
      <MainLayout
        aside={
          organization ? (
            <TechnologiesGridTile
              technologies={organization.technologies}
              title={`Technologies (${organization.technologies.length})`}
            />
          ) : undefined
        }
        columnsStartAtContainer="56rem"
      >
        <div className="flex flex-col gap-4">
          {isOrganizationPending ? (
            <LoadingStateContainer />
          ) : organization ? (
            <>
              <CompanyDetailsTile
                action="none"
                company={organization}
              />
              <ProductsGridTile
                products={organization.products}
                title={`Products (${organization.products.length})`}
              />
            </>
          ) : (
            <EmptyStateContainer
              className="min-h-72 rounded-xl border"
              message="This Organization is unavailable for your account"
              title="Organization Unavailable"
            />
          )}
        </div>
      </MainLayout>
    </PageviewTracker>
  );
};
